class HeaderMegamenu extends HTMLElement {
  constructor() {
    super();

    // Bind
    this.handleLinkHover = this.handleLinkHover.bind(this);
    this.openMegaMenu = this.openMegaMenu.bind(this);
    this.closeMegaMenu = this.closeMegaMenu.bind(this);
    this.maybeCloseMegaMenu = this.maybeCloseMegaMenu.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.onFocusIn = this.onFocusIn.bind(this);
    this.onResize = this.onResize.bind(this);

    // State
    this.isOverOpener = false;
    this.isOverMenu = false;
    this.closeDelayMs = 120;
    this.closeTimer = null;
    this.glideInstances = new Map(); // data-glide-class -> instance
    this.rafId = null;

    // Scroll
    this.onScroll = this.onScroll.bind(this);
    this.scrollCloseThreshold = 12;
    this._lastScrollY = window.scrollY || 0;
  }

  connectedCallback() {
    // Find external openers (siblings/parent area)
    this.menuOpeners =
      this.parentElement?.querySelectorAll?.(".menu_opener") || [];
    this.hoverArea =
      document.querySelector(".solstice__header-link-hover-area") || null;

    // Map keys -> content tabs
    this.contentKeys = Array.from(this.menuOpeners)
      .map((opener) => opener.dataset.key)
      .filter(Boolean);

    this.contentTabs = Object.fromEntries(
      this.contentKeys.map((key) => {
        // Use quoted attribute selector to be safe with special chars
        const tab = this.querySelector(
          `.solstice__header-megamenu-item[data-key="${CSS.escape(key)}"]`
        );
        return [key, tab || null];
      })
    );

    // Active tab
    this.activeTab =
      this.querySelector(".solstice__header-megamenu-item[active]") || null;

    // Carousels
    this.glides = this.querySelectorAll("[data-glide]");

    // Accessibility roles (light-touch; adjust to your markup)
    this.setAttribute("role", "region");
    this.setAttribute("aria-label", "Site menu");
    this.setAttribute("aria-hidden", "true");

    this.attachEvents();
    this.initGlides();
    this.observeSize();

    // Ensure initial height is correct if active tab exists
    this.style.height = "0px";
    this.setAttribute("aria-hidden", "true");
  }

  disconnectedCallback() {
    this.detachEvents();
    this.unmountGlides();
    this.unobserveSize();
    if (this.rafId) cancelAnimationFrame(this.rafId);
    if (this.closeTimer) clearTimeout(this.closeTimer);
  }

  // ----- Sizing -----
  updateMegaMenuHeight(immediate = false) {
    if (!this.classList.contains("open")) return; // <-- add this line
    const activeItem = this.querySelector(
      ".solstice__header-megamenu-item[active]"
    );
    const targetHeight = activeItem ? activeItem.scrollHeight : 0;
    if (immediate) {
      this.style.height = targetHeight + "px";
      return;
    }
    if (this.rafId) cancelAnimationFrame(this.rafId);
    this.rafId = requestAnimationFrame(() => {
      this.style.height = targetHeight + "px";
    });
  }

  onResize() {
    // Recompute height on viewport changes
    this.updateMegaMenuHeight();
  }

  observeSize() {
    // Adjust height when content inside the active tab changes size (images, fonts, carousel mount)
    this.resizeObserver = new ResizeObserver(() => this.updateMegaMenuHeight());
    this.resizeObserver.observe(this);
    // Fallback: update after images load (if any)
    this.querySelectorAll("img").forEach((img) => {
      if (!img.complete)
        img.addEventListener("load", () => this.updateMegaMenuHeight(), {
          once: true,
        });
    });
    window.addEventListener("resize", this.onResize);
  }

  unobserveSize() {
    this.resizeObserver?.disconnect();
    window.removeEventListener("resize", this.onResize);
  }

  // ----- Open/Close -----
  openMegaMenu() {
    this.classList.add("open");
    this.setAttribute("aria-hidden", "false");
    this.updateMegaMenuHeight();
  }

  closeMegaMenu() {
    this.classList.remove("open");
    this.setAttribute("aria-hidden", "true");
    this.style.height = "0px";
  }

  maybeCloseMegaMenu() {
    clearTimeout(this.closeTimer);
    this.closeTimer = setTimeout(() => {
      if (!this.isOverOpener && !this.isOverMenu) {
        this.closeMegaMenu();
      }
    }, this.closeDelayMs);
  }

  // ----- Tab switching -----
  handleLinkHover(event) {
    const key = event.currentTarget?.dataset?.key;
    const nextTab = key ? this.contentTabs[key] : null;
    if (!nextTab) return;

    if (this.activeTab) this.activeTab.removeAttribute("active");
    nextTab.setAttribute("active", "");
    this.activeTab = nextTab;

    // ARIA states on openers (helpful for SRs)
    this.menuOpeners.forEach((opener) => {
      opener.setAttribute(
        "aria-expanded",
        opener.dataset.key === key ? "true" : "false"
      );
      opener.setAttribute("aria-controls", key);
      opener.setAttribute("role", "menuitem");
      opener.setAttribute("tabindex", "0");
    });

    this.updateMegaMenuHeight();
  }

  // Keyboard: open on focus, arrow to switch, Escape to close
  onKeydown(e) {
    const openers = Array.from(this.menuOpeners);
    if (!openers.length) return;

    const currentIndex = openers.findIndex(
      (el) => el === document.activeElement
    );

    if (e.key === "Escape") {
      this.closeMegaMenu();
      openers[currentIndex]?.blur();
      return;
    }

    if (e.key === "ArrowRight" || e.key === "ArrowLeft") {
      e.preventDefault();
      let nextIndex = currentIndex;
      if (e.key === "ArrowRight")
        nextIndex = (currentIndex + 1 + openers.length) % openers.length;
      if (e.key === "ArrowLeft")
        nextIndex = (currentIndex - 1 + openers.length) % openers.length;
      const next = openers[nextIndex];
      if (next) {
        next.focus();
        // Treat as hover/select
        this.isOverOpener = true;
        this.handleLinkHover({ currentTarget: next });
        this.openMegaMenu();
      }
    }

    // if (e.key === "Enter") {
    //   // Space/Enter: simply ensure menu is open for the focused opener
    //   e.preventDefault();
    //   const focused = document.activeElement;
    //   if (focused && focused.classList.contains("menu_opener")) {
    //     this.isOverOpener = true;
    //     this.handleLinkHover({ currentTarget: focused });
    //     this.openMegaMenu();
    //   }
    // }
  }

  onFocusIn(e) {
    const opener = e.target?.closest?.(".menu_opener");
    if (opener) {
      this.isOverOpener = true;
      this.handleLinkHover({ currentTarget: opener });
      this.openMegaMenu();
    }
  }

  onScroll() {
    const y = window.scrollY || 0;
    if (this.classList.contains("open")) {
      if (Math.abs(y - this._lastScrollY) >= this.scrollCloseThreshold) {
        this.isOverOpener = false;
        this.isOverMenu = false;
        this.closeMegaMenu();
      }
    }
    this._lastScrollY = y;
  }

  // ----- Events -----
  attachEvents() {
    // Hover/focus on openers
    this._openerHandlers = [];
    this.menuOpeners.forEach((opener) => {
      const onEnter = () => {
        this.isOverOpener = true;
        this.handleLinkHover({ currentTarget: opener });
        this.openMegaMenu();
      };
      const onLeave = () => {
        this.isOverOpener = false;
        this.maybeCloseMegaMenu();
      };
      opener.addEventListener("mouseenter", onEnter);
      opener.addEventListener("mouseleave", onLeave);
      opener.addEventListener("focus", onEnter);
      this._openerHandlers.push([opener, onEnter, onLeave]);
    });

    // Menu hover
    this._onMenuEnter = () => {
      this.isOverMenu = true;
    };
    this._onMenuLeave = () => {
      this.isOverMenu = false;
      this.maybeCloseMegaMenu();
    };
    this.addEventListener("mouseenter", this._onMenuEnter);
    this.addEventListener("mouseleave", this._onMenuLeave);

    // Optional hover area
    if (this.hoverArea) {
      this._onHoverEnter = () => {
        this.isOverOpener = true;
      };
      this._onHoverLeave = () => {
        this.isOverOpener = false;
        this.maybeCloseMegaMenu();
      };
      this.hoverArea.addEventListener("mouseenter", this._onHoverEnter);
      this.hoverArea.addEventListener("mouseleave", this._onHoverLeave);
    }

    // Keyboard
    this._onKeydownDoc = this.onKeydown;
    this._onFocusInDoc = this.onFocusIn;
    document.addEventListener("keydown", this._onKeydownDoc);
    document.addEventListener("focusin", this._onFocusInDoc, true);

    // Scroll to close
    this._onScroll = this.onScroll;
    window.addEventListener("scroll", this._onScroll, { passive: true });
  }

  detachEvents() {
    // Openers
    if (this._openerHandlers) {
      this._openerHandlers.forEach(([opener, onEnter, onLeave]) => {
        opener.removeEventListener("mouseenter", onEnter);
        opener.removeEventListener("mouseleave", onLeave);
        opener.removeEventListener("focus", onEnter);
      });
      this._openerHandlers = null;
    }

    // Menu hover
    if (this._onMenuEnter)
      this.removeEventListener("mouseenter", this._onMenuEnter);
    if (this._onMenuLeave)
      this.removeEventListener("mouseleave", this._onMenuLeave);

    // Hover area
    if (this.hoverArea && this._onHoverEnter && this._onHoverLeave) {
      this.hoverArea.removeEventListener("mouseenter", this._onHoverEnter);
      this.hoverArea.removeEventListener("mouseleave", this._onHoverLeave);
    }

    // Keyboard
    if (this._onKeydownDoc)
      document.removeEventListener("keydown", this._onKeydownDoc);
    if (this._onFocusInDoc)
      document.removeEventListener("focusin", this._onFocusInDoc, true);

    // Scroll
    if (this._onScroll) {
      window.removeEventListener("scroll", this._onScroll);
      this._onScroll = null;
    }
  }

  // ----- Carousels -----
  initGlides() {
    document.addEventListener("DOMContentLoaded", () => {
      this.glides.forEach((glideElement) => {
        const cls = glideElement.dataset.glideClass;
        if (!cls || this.glideInstances.has(cls)) return;

        const selector = `.${cls}`;
        const node =
          this.querySelector(selector) || document.querySelector(selector);
        if (!node) return;

        const instance = new Glide(selector, {
          perView: 4,
          autoplay: 3000,
          type: "slider",
          focusAt: "0",
          bound: true,
          breakpoints: {
            1200: { perView: 3 },
            900: { perView: 2 },
            600: { perView: 1 },
          },
        });

        instance.on(["mount.after", "resize"], () =>
          this.updateMegaMenuHeight()
        );
        instance.mount();
        this.glideInstances.set(cls, instance);
      });
    });
  }

  unmountGlides() {
    this.glideInstances.forEach((instance) => {
      try {
        instance.destroy();
      } catch (_) {}
    });
    this.glideInstances.clear();
  }
}

customElements.define("header-megamenu", HeaderMegamenu);

class MobileMegamenuDrawer extends HTMLElement {
  constructor() {
    super();
  }
  connectedCallback() {
    this.opener = document.querySelector("#mobileMegamenuOpener");
    this.closer = document.querySelector("#mobileMegamenuCloser");
    this.mobileMegamenuWrapper = this;
    this.mobileMegamenuDrawer = this.querySelector("#mobileMegamenuDrawer");
    this.firstLevelLinks = this.querySelectorAll(
      "#mobileMegamenuDrawer ul.menu-level-1 > li"
    );
    this.firstLevelLinkedToSecondLevel = Array.from(this.firstLevelLinks).map(
      (link) => {
        return {
          firstLevel: link,
          secondLevel: this.querySelector(
            `ul.menu-level-2[data-key="${link.dataset.key}"]`
          ),
          backToFirst: this.querySelector(
            `span.back-to-first-list[data-key="${link.dataset.key}"]`
          ),
        };
      }
    );
    this.secondLevelLinkedToThirdLevel = Array.from(
      this.querySelectorAll("li[data-level-three-key]")
    ).map((link) => {
      const third_level_list = link.querySelector("ul[data-level-three-key]");
      if (third_level_list) {
        return {
          secondLevel: link,
          thirdLevel: third_level_list,
          backToSecond: this.querySelector(
            `span.back-to-second-list[data-level-three-key="${link.dataset.levelThreeKey}"]`
          ),
        };
      }
    });
    this.secondLevelGlides = this.querySelectorAll(".second-level-glide");
    this.backsToFirstList = this.querySelectorAll(".back-to-first-list");
    this.attachEvents();
  }

  closeAllSecondLevels() {
    this.firstLevelLinkedToSecondLevel.forEach(({ secondLevel }) => {
      secondLevel.classList.remove("visible");
    });
  }
  closeAllThirdLevels() {
    this.secondLevelLinkedToThirdLevel.forEach(({ thirdLevel }) => {
      thirdLevel.classList.remove("visible");
    });
  }

  open() {
    this.opener.classList.remove("visible");
    this.closer.classList.add("visible");
    this.mobileMegamenuWrapper.classList.add("open");
    document.body.classList.add("disable-body-scroll");
    window.scrollTo(0, 0);
    setTimeout(() => {
      this.mobileMegamenuDrawer.classList.add("open");
    }, 0);
  }
  close() {
    this.opener.classList.add("visible");
    this.closer.classList.remove("visible");
    this.mobileMegamenuDrawer.classList.remove("open");
    document.body.classList.remove("disable-body-scroll");
    setTimeout(() => {
      this.mobileMegamenuWrapper.classList.remove("open");
    }, 300);
  }
  initGlides() {
    if (!this.glideInstances) this.glideInstances = [];
    this.secondLevelGlides.forEach((glide) => {
      if (!glide._mounted) {
        const instance = new Glide(glide, {
          type: "slider",
          perView: 1,
        });
        instance.mount();
        this.glideInstances.push(instance);
        glide._mounted = true; // prevent double init
      }
    });
  }

  killGlides() {
    if (!this.glideInstances) return;

    this.glideInstances.forEach((instance) => {
      instance.destroy();
    });

    this.glideInstances = [];
  }
  attachEvents() {
    this.opener.addEventListener("click", (event) => this.open());
    this.closer.addEventListener("click", (event) => {
      this.closeAllSecondLevels();
      this.closeAllThirdLevels();
      this.close();
    });
    this.mobileMegamenuWrapper.addEventListener("click", (event) => {
      if (event.target.id == "mobileMegamenuWrapper") {
        this.close();
      }
    });
    this.firstLevelLinkedToSecondLevel.forEach(
      ({ firstLevel, secondLevel, backToFirst }) => {
        firstLevel.addEventListener("click", (event) => {
          secondLevel.classList.add("visible");
          this.initGlides();
        });
        backToFirst.addEventListener("click", (event) => {
          secondLevel.classList.remove("visible");
          this.killGlides();
        });
      }
    );

    this.secondLevelLinkedToThirdLevel.forEach(
      ({ secondLevel, thirdLevel, backToSecond }) => {
        secondLevel.addEventListener("click", (event) => {
          if (event.target.classList.contains("opener")) {
            thirdLevel.classList.add("visible");
            secondLevel.parentElement.classList.add("disableScroll");
            console.log(secondLevel.parentElement);
          }
        });
        backToSecond.addEventListener("click", (event) => {
          thirdLevel.classList.remove("visible");
          secondLevel.parentElement.classList.remove("disableScroll");
          console.log(secondLevel.parentElement);
        });
      }
    );
  }
}

customElements.define("mobile-megamenu-drawer", MobileMegamenuDrawer);

document.addEventListener("DOMContentLoaded", () => {
  // SEARCH
  const openSearchIcon = document.querySelector(
    ".site-nav__icons > a[href='/search']"
  );
  const closeSearchIcon = document.querySelector(".js-search-header-close");
  const searchContainer = document.querySelector("#searchContainer");
  const searchOverlay = document.querySelector("#searchOverlay");
  const searchSection = document.querySelector(
    ".solstice__header-search-section"
  );
  const searchInput = searchContainer?.querySelector("input[type='search']");
  const popularSearches =
    searchSection?.querySelectorAll("li.popular-search") || [];

  // Guards: if essentials are missing, bail quietly
  if (
    !openSearchIcon ||
    !closeSearchIcon ||
    !searchContainer ||
    !searchOverlay ||
    !searchSection
  )
    return;

  let lastScrollY = window.scrollY || 0;
  const scrollCloseThreshold = 12; // px before we close
  let openerToRestore = null;

  const isVisible = () => searchContainer.classList.contains("visible");

  function openSearch() {
    openerToRestore =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;

    searchContainer.classList.add("visible");
    searchSection.classList.add("visible");
    searchOverlay.classList.add("visible");

    // a11y hints
    searchContainer.setAttribute("aria-hidden", "false");
    openSearchIcon.setAttribute("aria-expanded", "true");
    // focus
    setTimeout(() => searchInput?.focus(), 0);
  }

  function closeSearch() {
    searchContainer.classList.remove("visible");
    searchSection.classList.remove("visible");
    searchOverlay.classList.remove("visible");

    searchContainer.setAttribute("aria-hidden", "true");
    openSearchIcon.setAttribute("aria-expanded", "false");

    // restore focus to the opener if possible
    openerToRestore?.focus?.();
    openerToRestore = null;
  }

  function toggleSearch() {
    isVisible() ? closeSearch() : openSearch();
  }

  // Open/close clicks
  openSearchIcon.addEventListener("click", (e) => {
    e.preventDefault();
    toggleSearch();
  });

  closeSearchIcon.addEventListener("click", (e) => {
    e.preventDefault();
    closeSearch();
  });

  // Overlay click to close (only when clicking the overlay background)
  searchOverlay.addEventListener("click", (e) => {
    if (e.target === searchOverlay) closeSearch();
  });

  // Close on Escape
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && isVisible()) {
      e.preventDefault();
      closeSearch();
    }
  });

  // Close on page scroll (beyond a small threshold)
  window.addEventListener(
    "scroll",
    () => {
      if (!isVisible()) {
        lastScrollY = window.scrollY || 0;
        return;
      }
      const y = window.scrollY || 0;
      if (Math.abs(y - lastScrollY) >= scrollCloseThreshold) {
        closeSearch();
      }
      lastScrollY = y;
    },
    { passive: true }
  );

  // Popular searches -> fill & focus input
  popularSearches.forEach((li) => {
    li.addEventListener("click", (e) => {
      const val = e.currentTarget.textContent.trim();

      if (searchInput) {
        searchInput.value = val;
        searchInput.focus();
        // trigger input event if your search code listens for it
        searchInput.dispatchEvent(new Event("input", { bubbles: true }));
      }
    });
  });

  // Glide
  const searchGlide = new Glide(
    ".solstice__header-search-section-glide-desktop",
    {
      perView: 2,
      bound: true,
      breakpoints: { 600: { perView: 1 } },
    }
  );
  searchGlide.mount();
});

document.addEventListener("DOMContentLoaded", () => {
  const bannerModal = document.querySelector("#bannerModal");
  const bannerModal_opener = document.querySelector(
    ".solstice__header-banner-left-wrapper p"
  );
  const desktop_bannerModal_opener = document.querySelector(
    ".solstice__header-banner-left-wrapper-top"
  );
  const bannerModal_close = document.querySelector("#headerBannerModal_close");

  if (bannerModal.hasAttribute("data-active-modal")) {
    // Check if banner modal has been closed in current session
    const bannerClosedInSession = sessionStorage.getItem("bannerModalClosed");

    // Auto-open modal if it hasn't been closed in current session
    // if (!bannerClosedInSession) {
    // openBannerModal();
    // }

    bannerModal_opener.addEventListener("click", () => {
      openBannerModal();
    });

    desktop_bannerModal_opener.addEventListener("click", () => {
      openBannerModal();
    });

    bannerModal.addEventListener("click", (event) => {
      if (event.currentTarget == event.target) {
        closeBannerModal();
      }
    });
    bannerModal_close.addEventListener("click", () => {
      closeBannerModal();
    });
  }
});

function openBannerModal() {
  bannerModal.classList.add("open");
}
function closeBannerModal() {
  bannerModal.classList.remove("open");
  // Set session storage flag to remember that banner was closed in this session
  sessionStorage.setItem("bannerModalClosed", "true");
}
